import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NOTIFICATION_SECTIONS, NOTIFICATION_TYPES, type NotificationPrefsPatch, type NotificationSection, type PushDevice } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { currentEndpoint, disablePush, enablePush, pushState, type PushState } from "../lib/push";
import { Group, Saved } from "../pages/club/ClubSettingsPage";
import { Sheet, Spinner } from "./ui";
import { AlertIcon, LockIcon, MailIcon, MoonIcon, PhoneIcon } from "./Icons";
import { AddressSheet, useMyEmails } from "./Emails";

/*
 * Settings → Notifications (PLAN.md §Notifications, and §Email addresses for the
 * channels; mock artifact 6Pet7JpXvLjCqv3zkhFP29): the channels first (this device's
 * push and the other devices, where emails go, quiet hours), then a table with a row
 * per kind of notification and a column per channel. The inbox gets everything and
 * has no column. Taps save at once and say so beside the name.
 */

const ABOUT: Record<NotificationSection, string> = {
  you: "Things meant for you. On to start with.",
  meetings: "Your clubs' meetings.",
  picking: "What your clubs read next.",
  running: "For owners and admins.",
  activity: "What's happening in your clubs. Off to start with.",
};

const DEVICE: Record<PushState, string> = {
  on: "This phone or browser gets notifications. Each device you use has its own switch.",
  off: "This phone or browser doesn't get notifications. Your inbox still has everything.",
  denied: "Notifications are blocked for Pagemates in this browser's settings. Allow them there, then come back.",
  "ios-install": "On iPhone and iPad, add Pagemates to your Home Screen first (in Safari: Share, then Add to Home Screen), then open it from there.",
  unsupported: "This browser can't get notifications. Your inbox still has everything.",
  unavailable: "This server doesn't send push notifications. Your inbox still has everything.",
};

const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const minutes = (v: string) => { const [h, m] = v.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };

export function NotificationSettings() {
  const qc = useQueryClient();
  const prefs = useQuery({ queryKey: ["notif-prefs"], queryFn: api.notificationPrefs });
  const [saved, setSaved] = useState<string | null>(null);
  const [sheet, setSheet] = useState<"address" | "devices" | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const flash = (k: string) => { setSaved(k); clearTimeout(timer.current); timer.current = setTimeout(() => setSaved(null), 1800); };
  const save = useMutation({ mutationFn: api.updateNotificationPrefs, onSuccess: (p) => qc.setQueryData(["notif-prefs"], p) });
  const put = (key: string, patch: NotificationPrefsPatch) => save.mutate(patch, { onSuccess: () => flash(key) });
  const emails = useMyEmails(Boolean(prefs.data?.emailAvailable));

  const [device, setDevice] = useState<PushState | null>(null);
  const [here, setHere] = useState<string | null>(null);
  const [deviceBusy, setDeviceBusy] = useState(false);
  useEffect(() => { void pushState().then(setDevice); void currentEndpoint().then(setHere); }, []);
  const toggleDevice = (on: boolean) => {
    setDeviceBusy(true);
    void (on ? enablePush() : disablePush()).then((s) => {
      setDevice(s); if (s === (on ? "on" : "off")) flash("device");
      void currentEndpoint().then(setHere); void qc.invalidateQueries({ queryKey: ["notif-prefs"] });
    }).finally(() => setDeviceBusy(false));
  };

  if (prefs.isPending) return <Group title="Notifications" about="What reaches you, and how."><Spinner /></Group>;
  if (!prefs.data) return <Group title="Notifications" about="What reaches you, and how."><p className="error">{describeError(prefs.error)}</p></Group>;
  const p = prefs.data;
  const quiet = p.quietStart !== null && p.quietEnd !== null;
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tick = (k: string) => <Saved on={saved === k} />;
  const others = p.devices.filter((d) => d.endpoint !== here);
  const thisDevice = p.devices.find((d) => d.endpoint === here);
  const pending = emails.data?.pending ?? null;
  // The email column works only with somewhere to send and the email switch on.
  const emailOn = p.emailAvailable && Boolean(p.notifyEmail) && p.email;
  const byKey = new Map(p.types.map((t) => [t.key, t]));

  return (
    <>
      <Group title="Notifications" about="Your inbox gets everything. These choose what also comes as a push or an email. They say who and where, never what was said.">
        <div className="chan" id="notifications">
          <span className="chan-ic"><PhoneIcon width={18} height={18} /></span>
          <div className="chan-main"><div className="k">Push on this device{tick("device")}</div>
            <div className="d">{device === "on" || device === "off" ? thisDevice?.label ?? "This browser" : device ? DEVICE[device] : "Checking…"}</div></div>
          {(device === "on" || device === "off") && (
            <input className="toggle" type="checkbox" checked={device === "on"} disabled={deviceBusy} aria-label="Push on this device" onChange={(e) => toggleDevice(e.target.checked)} />
          )}
        </div>
        {others.length > 0 && <button type="button" className="linkish small chan-more" onClick={() => setSheet("devices")}>Other devices: {others.length} ›</button>}

        {p.emailAvailable && <>
          <hr className="divider" />
          <div className="chan">
            <span className="chan-ic"><MailIcon width={18} height={18} /></span>
            <div className="chan-main">
              <div className="k">Send emails to{tick("email")}</div>
              {p.notifyEmail ? <>
                <div className="addr">{p.notifyEmail.address}</div>
                <span className="chips"><span className="chip-s ok">Confirmed</span>{p.notifyEmail.isRelay && <span className="chip-s">Hidden by Apple</span>}</span>
              </> : <div className="d">{p.emailUndeliverable ? "Nowhere right now." : "No address yet."}</div>}
            </div>
            {p.notifyEmail && <button type="button" className="btn sm" onClick={() => setSheet("address")}>Change</button>}
          </div>
          {pending && (
            <div className="chan-note">
              <div className="addr">{pending.address}</div>
              <span className="chip-s">Waiting for you to confirm</span>
              <div className="d">We sent it a code.{p.notifyEmail ? ` Emails keep going to ${p.notifyEmail.address} until then.` : ""}</div>
              <button type="button" className="linkish small" onClick={() => setSheet("address")}>Enter the code</button>
            </div>
          )}
          {p.notifyEmail?.isRelay && <p className="d" style={{ margin: 0 }}>Apple forwards these to your real inbox. You can use another address instead.</p>}
          {p.emailUndeliverable && (
            <div className="warn-note"><AlertIcon width={16} height={16} /><span>We couldn't deliver to your address, so we've stopped sending to it.</span>
              <button type="button" className="btn primary sm" onClick={() => setSheet("address")}>Choose another address</button></div>
          )}
          {!p.notifyEmail && !p.emailUndeliverable && !pending && <button type="button" className="btn primary sm" style={{ alignSelf: "flex-start" }} onClick={() => setSheet("address")}>Add an address</button>}
          {!p.email && (
            <div className="warn-note">
              <AlertIcon width={16} height={16} />
              <span>{p.emailComplained ? "One of our emails was marked as spam, so email is off. Your inbox and pushes carry on." : "Email is off: you stopped it from an email's link."}</span>
              <label className="chan" style={{ padding: 0 }}><span className="k" style={{ flex: 1 }}>Email</span>
                <input className="toggle" type="checkbox" checked={false} aria-label="Turn email back on" onChange={() => put("email", { email: true })} /></label>
            </div>
          )}
        </>}

        <hr className="divider" />
        <div className="chan">
          <span className="chan-ic"><MoonIcon width={18} height={18} /></span>
          <div className="chan-main"><div className="k">Quiet hours{tick("quiet")}</div><div className="d">Pushes wait, then arrive as one summary when quiet hours end. Email isn't held.</div></div>
          <input className="toggle" type="checkbox" checked={quiet} aria-label="Quiet hours"
            onChange={(e) => put("quiet", e.target.checked ? { quietStart: 22 * 60, quietEnd: 8 * 60 } : { quietStart: null, quietEnd: null })} />
        </div>
        {quiet && (
          <div className="sub-settings quiet-hours">
            <label>From <input className="input" type="time" value={hhmm(p.quietStart!)} onChange={(e) => e.target.value && put("quiet", { quietStart: minutes(e.target.value), quietEnd: p.quietEnd })} /></label>
            <label>until <input className="input" type="time" value={hhmm(p.quietEnd!)} onChange={(e) => e.target.value && put("quiet", { quietStart: p.quietStart, quietEnd: minutes(e.target.value) })} /></label>
          </div>
        )}
        <p className="muted small" style={{ margin: 0 }}>
          Times are {p.timeZone ? <>in <b>{p.timeZone.replace(/_/g, " ")}</b></> : "in your club owner's time zone until this browser tells us yours"}.{tick("tz")}
          {zone && zone !== p.timeZone && <> <button type="button" className="linkish" onClick={() => put("tz", { timeZone: zone })}>Use {zone.replace(/_/g, " ")}</button></>}
        </p>
        {save.isError && <p className="error small">{describeError(save.error)}</p>}
      </Group>

      <section className="sgroup ntab-group">
        <header><h2>What reaches you</h2><p>Your inbox gets everything. Choose what also comes as a push or an email.{!emailOn && p.emailAvailable ? " Add an email address to use the Email column." : ""}</p></header>
        <div className={`card ntab${p.emailAvailable ? "" : " no-email"}`}>
          <div className="ntab-head" aria-hidden="true"><span>Kind of notification</span><span>Push</span>{p.emailAvailable && <span>Email</span>}</div>
          {NOTIFICATION_SECTIONS.map((sec) => {
            const rows = NOTIFICATION_TYPES.filter((t) => t.section === sec.key && byKey.has(t.key));
            if (rows.length === 0) return null;
            return (
              <div key={sec.key} className="ntab-sec">
                <div className="ntab-sec-h">{sec.label}<span>{ABOUT[sec.key]}</span></div>
                {rows.map((t) => {
                  const c = byKey.get(t.key)!;
                  return (
                    <div key={t.key} className="ntab-row">
                      <div className="ntab-kind"><div className="k">{t.label}{tick(t.key)}</div><div className="d">{t.about}</div></div>
                      {c.push === null && c.email === null ? <div className="ntab-inbox">Inbox only</div> : <>
                        <Cell label="Push" kind={t.label} value={c.push} locked={c.locked} onChange={(v) => put(t.key, { types: { [t.key]: { push: v } } })} />
                        {p.emailAvailable && <Cell label="Email" kind={t.label} value={c.email} locked={c.locked} disabled={!emailOn}
                          onChange={(v) => put(t.key, { types: { [t.key]: { email: v } } })} />}
                      </>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        {p.mutedClubs.length > 0 && <p className="muted small" style={{ margin: 0 }}>{p.mutedClubs.length === 1 ? "1 club muted" : `${p.mutedClubs.length} clubs muted`}. Change that in each club's settings, under Your membership.</p>}
      </section>

      {sheet === "address" && <AddressSheet onClose={() => setSheet(null)} />}
      {sheet === "devices" && <DevicesSheet devices={p.devices} here={here} onClose={() => setSheet(null)} />}
    </>
  );
}

/** One switch in the table: labelled Push or Email beside it on phones; locked ones say "On". */
function Cell({ label, kind, value, locked, disabled, onChange }: { label: string; kind: string; value: boolean | null; locked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  if (value === null) return <div className="ntab-cell"><span className="ntab-cell-l">{label}</span><span className="muted" aria-label={`No ${label.toLowerCase()}`}>–</span></div>;
  if (locked) return <div className="ntab-cell"><span className="ntab-cell-l">{label}</span><span className="ntab-lock" title="Always sent, because it affects your account"><LockIcon width={12} height={12} />On</span></div>;
  return (
    <label className="ntab-cell"><span className="ntab-cell-l">{label}</span>
      <input className="toggle" type="checkbox" checked={value && !disabled} disabled={disabled} aria-label={`${label}: ${kind}`} onChange={(e) => onChange(e.target.checked)} /></label>
  );
}

/** "Other devices": each one that gets push, with Remove. */
function DevicesSheet({ devices, here, onClose }: { devices: PushDevice[]; here: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const remove = useMutation({ mutationFn: api.removeDevice, onSuccess: () => qc.invalidateQueries({ queryKey: ["notif-prefs"] }) });
  return (
    <Sheet label="Devices that get push" onClose={onClose}>
      <div className="prows">
        {devices.map((d) => (
          <div key={d.id} className="prow">
            <span className="pi"><PhoneIcon width={16} height={16} /></span>
            <div className="t">{d.label}<small>{d.endpoint === here ? "this device" : d.lastSuccessAt ? `last reached ${new Date(d.lastSuccessAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "not reached yet"}</small></div>
            {d.endpoint !== here && <button type="button" className="linkish small muted" disabled={remove.isPending} onClick={() => remove.mutate(d.id)}>remove</button>}
          </div>
        ))}
      </div>
      <p className="small muted" style={{ margin: 0 }}>A removed device stops getting push. Signing in there and turning push on adds it back.</p>
      {remove.isError && <p className="error small">{describeError(remove.error)}</p>}
    </Sheet>
  );
}

/** "Mute this club", in the club's own settings. Everything stops except someone asking to take the club over. */
export function MuteClub({ clubId }: { clubId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["club-mute", clubId], queryFn: () => api.clubMute(clubId) });
  const [saved, setSaved] = useState(false);
  const set = useMutation({
    mutationFn: (muted: boolean) => api.setClubMute(clubId, muted),
    onSuccess: (r) => { qc.setQueryData(["club-mute", clubId], r); void qc.invalidateQueries({ queryKey: ["notif-prefs"] }); setSaved(true); window.setTimeout(() => setSaved(false), 1800); },
  });
  return (
    <div className="switch">
      <div><div className="k">Mute this club<Saved on={saved} /></div><div className="d">Nothing from this club notifies you, except someone asking to take it over. Your inbox stays as it is.</div></div>
      <input className="toggle" type="checkbox" checked={q.data?.muted ?? false} disabled={q.isPending || set.isPending} aria-label="Mute this club"
        onChange={(e) => set.mutate(e.target.checked)} />
    </div>
  );
}
